import {
  Breadcrumbs,
  DATA_EXPLORER,
  DATA_EXPLORER_ICON,
  DATA_EXPLORER_ICON_COLOR,
  HeaderWithScrollables,
  IPerformanceReport,
  ISessionACL,
  layoutQuery,
  PageLayout,
  PageNames,
  useEnvironment,
  useLocationTrackingForPerformance,
  usePerformancePageStore,
} from '@diana/core';
import { DashboardPermissionTypes } from '@diana/core/build/components/PageLayout/ManageDashboards/ManageDashboards.types';
import { Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routeUrls } from '../../app/Routing/RouteUrls';
import { useBreadcrumbsRoot } from '../../hooks/useBreadcrumbsRoot';
import { PERFORMANCE_PAGE_NS } from './i18n';
import { PerformanceCard } from './PerformanceCard';

export const PerformancePage = () => {
  const navigate = useNavigate();
  const environment = useEnvironment();
  const { t } = useTranslation([PERFORMANCE_PAGE_NS]);
  const [isProcessing, setProcessing] = useState(false);
  const reports = usePerformancePageStore((store) => store.reports);
  const setReports = usePerformancePageStore((store) => store.setReports);

  const dataExplorerReport: IPerformanceReport = {
    id: DATA_EXPLORER,
    displayName: t('pages/PerformancePage:dataExplorerReport.title'),
    iconData: { icon: DATA_EXPLORER_ICON, color: DATA_EXPLORER_ICON_COLOR },
    permissionType: DashboardPermissionTypes.Ecosystem,
    acl: {} as ISessionACL,
    dianaDataType: 'Layout',
    users: [],
    tags: null,
    components: [],
    layoutType: 4,
    clonedFromId: '',
    flowId: '',
    permissions: [],
    originId: '',
    createdBy: '',
    columnsData: null,
    companies: [],
    componentsLength: 0,
    companyId: '',
  };

  const breadcrumbRoot = useBreadcrumbsRoot('performance');
  const performanceReports = layoutQuery.getPerformanceReports({ environment });
  const { data, isFetching, isFetched } = useQuery({
    queryKey: performanceReports.key,
    queryFn: performanceReports.fn,
  });

  useEffect(() => {
    if (!data) return;
    setReports(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const reportsToRender = [dataExplorerReport].concat(reports ?? []);

  const { track } = useLocationTrackingForPerformance();
  const navigateToDataExplorerReport = () => {
    navigate(routeUrls.performanceReport.url({ environment, reportId: DATA_EXPLORER }, null), {
      replace: true,
    });
  };

  useEffect(() => {
    if (!isFetched) return;
    if (isFetched && data?.length === 0) navigateToDataExplorerReport();
    else track();
    // eslint-disable-next-line
  }, [data]);

  return (
    <PageLayout
      name={PageNames.PERFORMANCE}
      title={breadcrumbRoot.title}
      isContentLoading={isFetching || isProcessing}
      header={<HeaderWithScrollables left={<Breadcrumbs items={[breadcrumbRoot]} />} />}
    >
      {reportsToRender?.length > 1 && (
        <Grid container rowSpacing={2} spacing={2}>
          {reportsToRender.map((report) => (
            <PerformanceCard
              id={report.id}
              iconData={report.iconData}
              key={report.id}
              displayName={report.displayName}
              setProcessing={setProcessing}
              acl={report.acl}
              users={report.users!}
              permissionType={report.permissionType}
            />
          ))}
        </Grid>
      )}
    </PageLayout>
  );
};
